import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import Link from 'next/link';
import { Flame, MessageSquarePlus, Bug, Lightbulb, ArrowLeft } from 'lucide-react';
import { useUserback } from '@userback/react';
import { Button } from '@/components/ui/button';
export default function EarlyPreviewPage() {
  const $ = _c(29);
  const userback = useUserback();
  let t0;
  if ($[0] !== userback) {
    t0 = () => {
      try {
        userback.open("general", "form");
      } catch {}
    };
    $[0] = userback;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleFeedback = t0;
  let t1;
  if ($[2] !== userback) {
    t1 = () => {
      try {
        userback.open("feature_request", "form");
      } catch {}
    };
    $[2] = userback;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleFeatureRequest = t1;
  let t2;
  if ($[4] !== userback) {
    t2 = () => {
      try {
        userback.open("bug", "form");
      } catch {}
    };
    $[4] = userback;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleBugReport = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Head><title>Early Preview — Sizzle</title><meta name="description" content="Sizzle is in early preview. Help us shape the future of food and beverage discovery." /></Head>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-3 mb-6"><div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center"><Flame className="w-5 h-5 text-white" /></div><div><h1 className="text-2xl font-bold text-gray-900">Sizzle is in Early Preview</h1></div></div>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="space-y-4 text-gray-600 leading-relaxed mb-10"><p>You're one of the first people using Sizzle. That means some things are rough around the edges, some features are half-built, and the occasional bug is lurking. That's by design.</p><p>We're building Sizzle in the open because the best products are shaped by the people who use them. Your feedback directly influences what we build next.</p><p>If something feels off, is missing, or could be better — we want to hear it.</p></div>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h2 className="text-lg font-semibold text-gray-900 mb-4">Tell us what you think</h2>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors"><MessageSquarePlus className="w-5 h-5 text-primary-600" /></div>;
    t8 = <div><div className="font-medium text-gray-900">General Feedback</div><div className="text-sm text-gray-500">Share thoughts on your experience</div></div>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  let t9;
  if ($[12] !== handleFeedback) {
    t9 = <button onClick={handleFeedback} className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all text-left cursor-pointer group">{t7}{t8}</button>;
    $[12] = handleFeedback;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-200 transition-colors"><Lightbulb className="w-5 h-5 text-amber-600" /></div>;
    t11 = <div><div className="font-medium text-gray-900">Suggest a Feature</div><div className="text-sm text-gray-500">What's missing? What would make Sizzle better?</div></div>;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  let t12;
  if ($[16] !== handleFeatureRequest) {
    t12 = <button onClick={handleFeatureRequest} className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all text-left cursor-pointer group">{t10}{t11}</button>;
    $[16] = handleFeatureRequest;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0 group-hover:bg-red-200 transition-colors"><Bug className="w-5 h-5 text-red-600" /></div>;
    t14 = <div><div className="font-medium text-gray-900">Report a Bug</div><div className="text-sm text-gray-500">Something broken? Let us know and we'll fix it fast</div></div>;
    $[18] = t13;
    $[19] = t14;
  } else {
    t13 = $[18];
    t14 = $[19];
  }
  let t15;
  if ($[20] !== handleBugReport) {
    t15 = <button onClick={handleBugReport} className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all text-left cursor-pointer group">{t13}{t14}</button>;
    $[20] = handleBugReport;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t12 || $[23] !== t15 || $[24] !== t9) {
    t16 = <div className="space-y-3 mb-10">{t6}{t9}{t12}{t15}</div>;
    $[22] = t12;
    $[23] = t15;
    $[24] = t9;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="rounded-xl bg-gray-50 border border-gray-100 p-6"><p className="text-sm text-gray-500">You can also reach us anytime at{" "}<a href="mailto:hello@sizzle.nz" className="text-primary-600 hover:underline cursor-pointer">hello@sizzle.nz</a></p></div>;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== t16) {
    t18 = <>{t3}<div className="max-w-2xl mx-auto px-4 py-12 sm:py-16">{t4}{t5}{t16}{t17}</div></>;
    $[27] = t16;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  return t18;
}